import React from 'react';
import { Text } from '@ui-library';
import { Tooltip } from '../Tooltip/Tooltip';
import { ColumnResizeHandle, type ColumnResizeHandleProps } from '../ColumnResize';

const ROW_INDEX_WIDTH = 40;
const DEFAULT_COL_WIDTH = 160;
const SKELETON_CELL_WIDTHS = ['w-full', 'w-3/4', 'w-1/2', 'w-5/6', 'w-2/3', 'w-full', 'w-3/5', 'w-4/5'];
const SKELETON_HEAD_WIDTHS = ['w-20', 'w-16', 'w-24', 'w-14', 'w-20', 'w-12'];
/** Trailing unsized column that absorbs spare width; keeps the row lines running to the edge. */
const FILLER_CELL_CLASS = 'p-0 border-y border-border-primary';

export interface DataTableColumn {
  name: string;
  type?: string;
}

export interface DataTableProps {
  columns: DataTableColumn[];
  rows: Record<string, any>[];
  columnWidths: Record<string, number>;
  /** From `useColumnResize().handleProps`. Omit to render the columns fixed. */
  getResizeHandleProps?: (colKey: string) => Omit<ColumnResizeHandleProps, 'label'>;
  startRowIndex?: number;
  onCellContextMenu?: (e: React.MouseEvent, value: any) => void;
  /** When provided, wraps each data cell in a Tooltip with this content (e.g. value + Copy button). Use instead of onCellContextMenu for copy UX. */
  renderCellTooltipContent?: (value: any, onClose: () => void) => React.ReactNode;
  /** 'hover' = show tooltip on hover, 'click' = show on cell click */
  tooltipTrigger?: 'hover' | 'contextmenu';
  isLoading?: boolean;
  getColumnKey?: (col: DataTableColumn) => string;
}

const DataCell = ({ value }: { value: any }) => {
  if (value === null || value === undefined) {
    return <span className='text-xs italic text-fg-tertiary'>NULL</span>;
  }
  if (typeof value === 'boolean') {
    return (
      <span className={`text-[11px] font-medium px-1.5 py-px rounded ${value ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'}`}>
        {value ? 'true' : 'false'}
      </span>
    );
  }
  return (
    <span className='text-xs text-fg-primary truncate block' title={String(value)}>
      {String(value)}
    </span>
  );
};

function SkeletonTable({ colCount }: { colCount: number }) {
  return (
    <div className='flex-1 overflow-hidden'>
      <table className='border-collapse text-left w-full' style={{ tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: ROW_INDEX_WIDTH }} />
          {Array.from({ length: colCount }).map((_, i) => <col key={i} style={{ width: DEFAULT_COL_WIDTH }} />)}
          <col />
        </colgroup>
        <thead className='sticky top-0 z-10 bg-bg-secondary'>
          <tr>
            <th className='px-bg-v2-md py-bg-v2-sm border border-border-primary' style={{ maxWidth: 0, overflow: 'hidden' }} />
            {Array.from({ length: colCount }).map((_, i) => (
              <th key={i} className='px-bg-v2-md py-bg-v2-sm border border-border-primary' style={{ maxWidth: 0, overflow: 'hidden' }}>
                <div className='flex flex-col gap-1'>
                  <div className={`h-3 rounded skeleton-shimmer ${SKELETON_HEAD_WIDTHS[i % 6]}`} />
                  <div className='h-3.5 rounded skeleton-shimmer w-10' />
                </div>
              </th>
            ))}
            <th aria-hidden className={FILLER_CELL_CLASS} />
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 14 }).map((_, rowIdx) => (
            <tr key={rowIdx}>
              <td className='px-bg-v2-md py-bg-v2-sm border border-border-primary bg-bg-secondary' style={{ maxWidth: 0, overflow: 'hidden' }}>
                <div className='h-3 rounded skeleton-shimmer w-4 ml-auto' />
              </td>
              {Array.from({ length: colCount }).map((_, colIdx) => (
                <td key={colIdx} className='px-bg-v2-md py-bg-v2-sm border border-border-primary' style={{ maxWidth: 0, overflow: 'hidden' }}>
                  <div className={`h-3 rounded skeleton-shimmer ${SKELETON_CELL_WIDTHS[(colIdx + rowIdx) % SKELETON_CELL_WIDTHS.length]}`} />
                </td>
              ))}
              <td aria-hidden className={FILLER_CELL_CLASS} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  columnWidths,
  getResizeHandleProps,
  startRowIndex = 1,
  onCellContextMenu,
  renderCellTooltipContent,
  tooltipTrigger = 'hover',
  isLoading = false,
  getColumnKey = (col) => col.name,
}) => {
  const getColWidth = (colKey: string) => columnWidths[colKey] ?? DEFAULT_COL_WIDTH;

  if (isLoading) {
    return <SkeletonTable colCount={columns.length || 5} />;
  }

  if (columns.length === 0) {
    return null;
  }

  return (
    <div className='flex-1 overflow-x-auto overflow-y-auto modern-scrollbar-spaced'>
      {/* Fixed layout at 100%: a fixed table grows past its width when the
          columns sum to more, so it scrolls; when they sum to less, the
          trailing unsized column takes the slack and every real column stays
          exactly the width it was dragged to. */}
      <table className='border-collapse text-left w-full' style={{ tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: ROW_INDEX_WIDTH }} />
          {columns.map((col) => (
            <col key={getColumnKey(col)} style={{ width: getColWidth(getColumnKey(col)) }} />
          ))}
          <col />
        </colgroup>
        <thead className='sticky top-0 z-10 bg-bg-secondary'>
          <tr>
            <th className='px-bg-v2-md py-bg-v2-sm border border-border-primary text-right select-none' style={{ maxWidth: 0, overflow: 'hidden' }}>
              <Text variant='text-xs' color='tertiary'>#</Text>
            </th>
            {columns.map((col) => {
              const colKey = getColumnKey(col);
              return (
                <th
                  key={colKey}
                  className='px-bg-v2-md py-bg-v2-sm border border-border-primary text-left relative select-none'
                  style={{ maxWidth: 0, overflow: 'hidden' }}
                >
                  <div className='flex flex-col gap-0.5 items-start w-full text-left overflow-hidden'>
                    <span className='text-xs font-semibold text-fg-primary truncate block w-full'>{col.name}</span>
                    {col.type != null && (
                      <span className='text-[10px] font-medium px-1 py-px rounded uppercase tracking-wide text-fg-tertiary bg-bg-primary shrink-0'>
                        {col.type}
                      </span>
                    )}
                  </div>
                  {getResizeHandleProps && (
                    <ColumnResizeHandle {...getResizeHandleProps(colKey)} label={`Resize ${col.name} column`} />
                  )}
                </th>
              );
            })}
            <th aria-hidden className={FILLER_CELL_CLASS} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx} className='hover:bg-bg-secondary transition-colors'>
              <td className='px-bg-v2-md py-bg-v2-sm border border-border-primary text-right select-none bg-bg-secondary' style={{ maxWidth: 0, overflow: 'hidden' }}>
                <Text variant='text-xs' color='tertiary'>{startRowIndex + rowIdx}</Text>
              </td>
              {columns.map((col) => {
                const colKey = getColumnKey(col);
                const value = row[col.name];
                const cellContent = <DataCell value={value} />;
                return (
                  <td
                    key={colKey}
                    className='px-bg-v2-md py-bg-v2-sm border border-border-primary cursor-default'
                    style={{ maxWidth: 0, overflow: 'hidden' }}
                    onContextMenu={renderCellTooltipContent ? undefined : onCellContextMenu ? (e) => onCellContextMenu(e, value) : undefined}
                  >
                    {renderCellTooltipContent ? (
                      <Tooltip
                        content={(onClose) => renderCellTooltipContent(value, onClose)}
                        position='top'
                        interactive
                        delay={tooltipTrigger === 'hover' ? 200 : 0}
                        trigger={tooltipTrigger}
                      >
                        <span className='block truncate'>{cellContent}</span>
                      </Tooltip>
                    ) : (
                      cellContent
                    )}
                  </td>
                );
              })}
              <td aria-hidden className={FILLER_CELL_CLASS} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
